import * as React from 'react'
import { usePickingListsQuery } from '../../hooks/queries/picking_lists'
import usePickingListTableController from '../../hooks/usePickingListTableController'
import ErrorMessage from '../job_plannings/list_app/ErrorMessage'
import PickingListTableView from './PickingListTableView'
import PickingListCardsView from './PickingListCardsView'
import ViewSwitcher from '../shared/ViewSwitcher'
import { JobNumberShape } from '../../api/api_data_types'
import SaveAllPickingListLinesButton from './SaveAllPickingListLinesButton'
import AddPickingListLineButton from './AddPickingListLineButton'

type PickingListProps = {
  jobnumber: JobNumberShape
  currentUser: { username: string }
}

export default function PickingList({ jobnumber, currentUser }: PickingListProps) {
  const jobId = jobnumber.job_id
  const { isLoading, isError, data, error } = usePickingListsQuery(jobId)
  const {
    saveAllError,
    setSaveAllError,
    submitResult,
    setSubmitResult,
    changedQuantities,
    setChangedQuantities,
    currentView,
    setCurrentView,
  } = usePickingListTableController()

  React.useEffect(() => {
    setSaveAllError(null)
    setSubmitResult(null)
    setChangedQuantities({})
  }, [jobId, setChangedQuantities, setSaveAllError, setSubmitResult])

  React.useEffect(() => {
    if (!data) return

    const lockedKeys = data.filter((row) => row.row_type === 'calculation' && row.submitted).map((row) => row.key)
    if (lockedKeys.length > 0) {
      setChangedQuantities((current) => {
        const next = { ...current }
        lockedKeys.forEach((rowKey) => {
          delete next[rowKey]
        })
        return next
      })
    }
  }, [data, setChangedQuantities])

  if (isError && !data) {
    return <ErrorMessage isError={isError} error={error} />
  }

  return (
    <div>
      <div className="picking-list-top-actions d-flex justify-content-between align-items-center mb-3">
        <div className="picking-list-feedback">
          <ErrorMessage error={saveAllError} isError={saveAllError !== null} />
          {submitResult && (
            <div className="picking-list-submit-result">
              {submitResult.succeeded.length > 0 && (
                <div className="text-success">
                  {submitResult.succeeded.length} varer gemt til eksport: {submitResult.succeeded.join(', ')}
                </div>
              )}
              {submitResult.skipped.length > 0 && (
                <div className="text-muted">
                  Allerede registreret: {submitResult.skipped.join(', ')}
                </div>
              )}
              {submitResult.failed.length > 0 && (
                <div className="text-danger">
                  Fejlede: {submitResult.failed.map((failure) => `${failure.item_no} - ${failure.error}`).join('; ')}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="picking-list-actions d-flex align-items-center">
          <AddPickingListLineButton jobId={jobId} currentUser={currentUser} />
          <SaveAllPickingListLinesButton
            jobId={jobId}
            changedQuantities={changedQuantities}
            setChangedQuantities={setChangedQuantities}
            setError={setSaveAllError}
            setSubmitResult={setSubmitResult}
          />
          <div className="ml-2">
            <ViewSwitcher currentView={currentView} setCurrentView={setCurrentView} />
          </div>
        </div>
      </div>

      <div className="picking-list-content">
        {currentView === 'table' && (
          <PickingListTableView
            data={data}
            currentUser={currentUser}
            isLoading={isLoading}
            changedQuantities={changedQuantities}
            setChangedQuantities={setChangedQuantities}
          />
        )}

        {currentView === 'cards' && (
          <PickingListCardsView
            data={data}
            currentUser={currentUser}
            isLoading={isLoading}
            changedQuantities={changedQuantities}
            setChangedQuantities={setChangedQuantities}
          />
        )}
      </div>
    </div>
  )
}
